import React from "react";
import C3Chart from "react-c3js";
import moment from "moment";
import clientService from "../../../services/client";
import ChartDateRangeForm from "./ChartDateRangeForm";
import AnalyticsChartCard from "./AnalyticsChartCard";
import ContextServicePills from "./ContextServicePills";
import { defaultStartDate, categoryAxis } from "./chartUtils";

const _ = require("lodash");

const emptyChart = {
  bindto: "#traffic-analysis",
  x: "x",
  columns: [["x", null], ["Total Users", null], ["Total Unique Users", null]],
  colors: {
    "Total Users": "#0A49B6",
    "Total Unique Users": "#fd9644",
  },
};

export default class TrafficAnalysisChart extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      startDate: defaultStartDate(),
      endDate: new Date(),
      filterStartDate: defaultStartDate().toLocaleDateString("en-US"),
      filterEndDate: new Date().toLocaleDateString("en-US"),
      contextKey: [],
      contextUniqueData: [],
      contextData: [],
      sortValue: "byDay",
      chartData: emptyChart,
    };
  }

  componentDidMount() {
    this.fetchData();
  }

  buildData(data, keyName) {
    const result = [];
    _.forEach(data, (val, key) => {
      const totalCounts = val.reduce((acc, curr) => acc + curr.count, 0);
      result.push({ [keyName]: key, count: totalCounts });
    });
    return result;
  }

  groupAndBuild(data, dateMethod, groupKey) {
    const groupedData = _.groupBy(data, (d) => d.dateObj[dateMethod]());
    return this.buildData(groupedData, groupKey);
  }

  buildChart(xAxisData, countDataContext, countDataUnique) {
    if (xAxisData && xAxisData.length) {
      return {
        bindto: "#traffic-analysis",
        x: "x",
        columns: [
          ["x", ...xAxisData],
          ["Total Users", ...countDataContext],
          ["Total Unique Users", ...countDataUnique],
        ],
        colors: {
          "Total Users": "#0A49B6",
          "Total Unique Users": "#fd9644",
        },
      };
    }
    return emptyChart;
  }

  async fetchData() {
    const { filterStartDate, filterEndDate, contextKey } = this.state;
    if (!filterStartDate || !filterEndDate) {
      return;
    }

    try {
      const uniqueRes = await clientService.getChartData(
        filterStartDate,
        filterEndDate,
        true,
        contextKey,
      );
      const uniqueData = Array.isArray(uniqueRes && uniqueRes.data)
        ? uniqueRes.data
        : [];
      uniqueData.forEach((d) => {
        d.dateObj = moment(d._id);
      });

      const allRes = await clientService.getChartData(
        filterStartDate,
        filterEndDate,
        false,
        contextKey,
      );
      const allData = Array.isArray(allRes && allRes.data) ? allRes.data : [];
      allData.forEach((d) => {
        d.dateObj = moment(d._id);
      });

      const xAxisData = uniqueData.length ? uniqueData.map((a) => a._id) : [];
      const countDataUnique = uniqueData.length
        ? uniqueData.map((a) => a.count)
        : [];
      const countDataContext = allData.length ? allData.map((a) => a.count) : [];

      this.setState({
        contextUniqueData: uniqueData,
        contextData: allData,
        chartData: this.buildChart(xAxisData, countDataContext, countDataUnique),
      });
    } catch (err) {
      console.error(err);
    }
  }

  handleDateChange = (type, date) => {
    const changeDate = new Date(date).toLocaleDateString("en-US");
    if (type === "startDate") {
      this.setState({ startDate: date, filterStartDate: changeDate });
    } else {
      this.setState({ endDate: date, filterEndDate: changeDate });
    }
  };

  handleSubmit = (event) => {
    event.preventDefault();
    this.setState({ sortValue: "byDay" }, () => this.fetchData());
  };

  toggleContext = (key) => {
    const { contextKey } = this.state;
    const nextKeys = contextKey.indexOf(key) < 0
      ? contextKey.concat(key)
      : contextKey.filter((item) => item !== key);
    this.setState({ contextKey: nextKeys, sortValue: "byDay" }, () =>
      this.fetchData(),
    );
  };

  sortGraph = (value) => {
    const { contextUniqueData, contextData } = this.state;
    this.setState({ sortValue: value });

    if (value === "byWeek" && contextUniqueData.length && contextData.length) {
      const uniqueGrouped = this.groupAndBuild(contextUniqueData, "week", "week");
      const allGrouped = this.groupAndBuild(contextData, "week", "week");
      const newData = uniqueGrouped.map((el) =>
        el.week ? Object.assign({}, el, { week: "week-" + el.week }) : el,
      );
      this.setState({
        chartData: this.buildChart(
          newData.map((a) => a.week),
          allGrouped.map((a) => a.count),
          newData.map((a) => a.count),
        ),
      });
      return;
    }

    if (value === "byMonth" && contextUniqueData.length && contextData.length) {
      const uniqueGrouped = this.groupAndBuild(
        contextUniqueData,
        "month",
        "month",
      );
      const allGrouped = this.groupAndBuild(contextData, "month", "month");
      const newData = uniqueGrouped.map((el) =>
        el.month
          ? Object.assign({}, el, { month: "month-" + (parseInt(el.month) + 1) })
          : el,
      );
      this.setState({
        chartData: this.buildChart(
          newData.map((a) => a.month),
          allGrouped.map((a) => a.count),
          newData.map((a) => a.count),
        ),
      });
      return;
    }

    this.fetchData();
  };

  render() {
    return (
      <React.Fragment>
        <ChartDateRangeForm
          startDate={this.state.startDate}
          endDate={this.state.endDate}
          onStartChange={(date) => this.handleDateChange("startDate", date)}
          onEndChange={(date) => this.handleDateChange("endDate", date)}
          onSubmit={this.handleSubmit}
        />
        <AnalyticsChartCard
          title="Traffic Analysis"
          headerRight={
            <ContextServicePills
              contexts={this.props.contexts}
              onSelect={this.toggleContext}
            />
          }
          footer={
            <div className="sortvalue drop-1">
              <select
                className="form-control custom-select auto1"
                onChange={(e) => this.sortGraph(e.target.value)}
                value={this.state.sortValue}
              >
                <option value="byDay">By Day</option>
                <option value="byWeek">By Week</option>
                <option value="byMonth">By Month</option>
              </select>
            </div>
          }
        >
          <C3Chart
            data={this.state.chartData}
            size={{ height: 340 }}
            transition={{ duration: 1000 }}
            axis={categoryAxis}
            padding={{ bottom: 0, top: 0 }}
          />
        </AnalyticsChartCard>
      </React.Fragment>
    );
  }
}
